import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import {
  invoicesApi,
  clientsApi,
  invoiceSettingsApi,
  extractList,
  INVOICE_STATUS_OPTIONS,
  type InvoiceStatus,
} from '../../api/facades';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

type LineItem = { description: string; qty: string; rate: string; tax: string };

const InvoiceCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [client_id, setClientId] = useState<number | ''>('');
  const [invoice_no, setInvoiceNo] = useState('');
  const [inv_status, setInvStatus] = useState<InvoiceStatus>('Due');
  const [due_date, setDueDate] = useState('');
  const [inv_date, setInvDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState<LineItem[]>([{ description: '', qty: '1', rate: '0', tax: '0' }]);

  const { data: clientsRes } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.list({}),
  });
  const clients = extractList(clientsRes);

  const { data: settingsRes } = useQuery({
    queryKey: ['invoice-settings-current'],
    queryFn: () => invoiceSettingsApi.current(),
  });
  const settingsData = (settingsRes as any)?.data?.data ?? (settingsRes as any)?.data;

  useEffect(() => {
    if (!settingsData) return;
    if (!invoice_no && settingsData.next_invoice_no) setInvoiceNo(settingsData.next_invoice_no);
    if (!remarks && settingsData.client_note) setRemarks(settingsData.client_note);
  }, [settingsData, invoice_no, remarks]);

  const numericItems = useMemo(() => {
    return items.map((item) => {
      const qty = parseInt(item.qty || '0', 10) || 0;
      const rate = parseFloat(item.rate || '0') || 0;
      const tax = parseFloat(item.tax || '0') || 0;
      const amount = qty * rate;
      return { qty, rate, tax, amount, description: item.description };
    });
  }, [items]);
  const subTotal = numericItems.reduce((sum, it) => sum + it.amount, 0);
  const taxTotal = numericItems.reduce((sum, it) => sum + it.tax, 0);
  const total = subTotal + taxTotal;

  const create = useMutation({
    mutationFn: () =>
      invoicesApi.create({
        client_id: client_id as number,
        invoice_no,
        inv_date,
        due_date,
        inv_status,
        remarks: remarks || null,
        items: numericItems.map((it) => ({
          description: it.description || undefined,
          qty: it.qty,
          rate: it.rate,
          tax: it.tax,
        })),
      }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      const id = data?.data?.id ?? (data?.data && (data.data as any).id);
      if (id) navigate(`/dashboard/invoices/${id}`);
      else navigate('/dashboard/invoices');
      toast.success('Invoice created.');
    },
    onError: (e: any) => toast.error(e?.message || 'Create failed.'),
  });

  const canSubmit =
    typeof client_id === 'number' &&
    invoice_no.trim().length > 0 &&
    inv_date.trim().length > 0 &&
    due_date.trim().length > 0 &&
    items.length > 0;

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">New Invoice</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          create.mutate();
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Client" hint="Required">
            <select
              className={inputClass}
              value={client_id === '' ? '' : client_id}
              onChange={(e) => setClientId(e.target.value === '' ? '' : Number(e.target.value))}
              required
            >
              <option value="">Select client</option>
              {clients.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Invoice no.">
            <input className={inputClass} value={invoice_no} onChange={(e) => setInvoiceNo(e.target.value)} required />
          </FormField>
          <FormField label="Status">
            <select className={inputClass} value={inv_status} onChange={(e) => setInvStatus(e.target.value as InvoiceStatus)}>
              {INVOICE_STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Invoice date">
            <input className={inputClass} type="date" value={inv_date} onChange={(e) => setInvDate(e.target.value)} required />
          </FormField>
          <FormField label="Due date">
            <input className={inputClass} type="date" value={due_date} onChange={(e) => setDueDate(e.target.value)} required />
          </FormField>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Line items</div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800/80">
                <tr>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right w-20">Qty</th>
                  <th className="px-3 py-2 text-right w-24">Rate</th>
                  <th className="px-3 py-2 text-right w-24">Tax</th>
                  <th className="px-3 py-2 text-right w-28">Line total</th>
                  <th className="px-3 py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const it = numericItems[idx];
                  return (
                    <tr key={idx} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="px-3 py-2">
                        <textarea
                          className={`${inputClass} min-h-[40px]`}
                          rows={2}
                          value={item.description}
                          onChange={(e) => {
                            const copy = [...items];
                            copy[idx] = { ...copy[idx], description: e.target.value };
                            setItems(copy);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          className={inputClass}
                          type="number"
                          min={1}
                          value={item.qty}
                          onChange={(e) => {
                            const copy = [...items];
                            copy[idx] = { ...copy[idx], qty: e.target.value };
                            setItems(copy);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          className={inputClass}
                          type="number"
                          step="0.01"
                          min={0}
                          value={item.rate}
                          onChange={(e) => {
                            const copy = [...items];
                            copy[idx] = { ...copy[idx], rate: e.target.value };
                            setItems(copy);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          className={inputClass}
                          type="number"
                          step="0.01"
                          min={0}
                          value={item.tax}
                          onChange={(e) => {
                            const copy = [...items];
                            copy[idx] = { ...copy[idx], tax: e.target.value };
                            setItems(copy);
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">{it ? (it.amount + it.tax).toFixed(2) : '0.00'}</td>
                      <td className="px-3 py-2 text-center">
                        {items.length > 1 && (
                          <button
                            type="button"
                            className="text-xs text-red-600"
                            onClick={() => setItems(items.filter((_, i) => i !== idx))}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setItems((prev) => [...prev, { description: '', qty: '1', rate: '0', tax: '0' }])}
            >
              + Add line
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormField label="Remarks">
              <textarea className={inputClass} rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </FormField>
          </div>
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-sm space-y-1">
            <div className="flex justify-between"><span>Sub total</span><span>{subTotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Tax</span><span>{taxTotal.toFixed(2)}</span></div>
            <div className="flex justify-between font-semibold"><span>Total</span><span>{total.toFixed(2)}</span></div>
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!canSubmit || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceCreatePage;
